import { useEffect, useRef, useState } from 'react';
import AccountButton from '@/components/Header/AccountButton';
import CartButton from '@/components/Header/CartButton';
import DesktopNavigation from '@/components/Header/DesktopNavigation';
import MobileMenu from '@/components/Header/MobileMenu';
import { buildUserMenus, getNavigationForRole } from '@/components/Header/navigation';
import NotificationButton from '@/components/Header/NotificationButton';
import { UserRole } from '@/models/User';
import { useAuth } from '@/modules/auth/contexts/AuthContext';
import { useCart } from '@/modules/store/contexts/CartContext';
import { HomePath } from '@/routing/paths';
import { Bars3Icon } from '@heroicons/react/24/outline';
import { Link, useNavigate } from 'react-router-dom';

const Header = () => {
  const navigate = useNavigate();

  const shouldNavigateAfterRoleChange = useRef(false);

  const { role, roles, changeRole } = useAuth();
  const { cartItemCount } = useCart();

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const showCartButton = role === UserRole.StoreManager;

  const navigation = getNavigationForRole(role);

  const { userMenu, accountMenu } = buildUserMenus({
    role,
    roles,
    changeRole,
    onRoleChangeStart: () => {
      shouldNavigateAfterRoleChange.current = true;
    },
  });

  const openMobileMenu = () => {
    setMobileMenuOpen(true);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  useEffect(() => {
    if (shouldNavigateAfterRoleChange.current) {
      navigate(HomePath);
      shouldNavigateAfterRoleChange.current = false;
    }
  }, [role, navigate]);

  return (
    <header className="bg-white shadow">
      <nav
        className="mx-auto flex max-w-7xl items-center justify-between px-4 lg:py-1 lg:px-8 min-h-14"
        aria-label="Global"
      >
        <div className="flex lg:flex-1 items-center">
          <Link to={HomePath} className="flex gap-2">
            <img className="h-8 w-auto" src="/logo.svg" alt="" />
            <div className="flex">
              <span className="font-semibold self-center text-[16px]">Simpler</span>
              <span className="font-semibold self-center text-[16px] ml-0.5">Supply</span>
            </div>
          </Link>
        </div>
        <DesktopNavigation navigation={navigation} />
        <div className="flex flex-1 justify-end gap-3 md:gap-1">
          <div className="md:mr-1.5">
            <NotificationButton />
          </div>

          <div className="hidden md:block">
            <AccountButton menuItems={accountMenu} />
          </div>

          {showCartButton && <CartButton count={cartItemCount} />}
        </div>
        <div className="flex lg:hidden ml-4">
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-md text-gray-700"
            onClick={openMobileMenu}
          >
            <span className="sr-only">Open main menu</span>
            <Bars3Icon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>
      </nav>

      <MobileMenu
        open={mobileMenuOpen}
        close={closeMobileMenu}
        menu={navigation}
        userMenu={userMenu}
        cartItemCount={cartItemCount}
      />
    </header>
  );
};

export default Header;
